import type { Trace } from '@/features/traceability/type/Tool';
import type { Machine } from '@/features/traceability/type/Machine';
import type { Material } from '@/features/traceability/type/Material';
import type { MaterialLot } from '@/features/traceability/type/MaterialLot';

/**
 * Parse an "HH:MM:SS" duration string to total seconds.
 * Returns null for empty/undefined input.
 */
function parseDuration(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parts = value.split(':');
  if (parts.length !== 3) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parseInt(parts[2], 10);
  if (isNaN(h) || isNaN(m) || isNaN(s)) return null;
  return h * 3600 + m * 60 + s;
}

function asString(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

function asNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => typeof v === 'string') as string[];
}

/**
 * Build a Trace object from flat editValues (same keys as the Form page).
 * Duration strings "HH:MM:SS" are parsed to seconds.
 * Uses full Machine/Material/MaterialLot objects carried through editValues.
 */
export function buildTraceFromValues(values: Record<string, unknown>, traceId: number): Trace {
  // The flat machine fields are the source of truth: they are always seeded from
  // the trace and always updated when the user picks another machine. The seeded
  // 'machine' object may be stale, so it is only a fallback when no flat name exists.
  const machineId = values['machineId'] != null ? Number(values['machineId']) : undefined;
  const flatMachine: Machine = {
    ...(machineId !== undefined && !isNaN(machineId) ? { id: machineId } : {}),
    name: asString(values['machineName']) ?? '',
    brand: asString(values['machineBrand']) ?? '',
    model: asString(values['machineModel']) ?? '',
    serialNumber: asString(values['machineSerial']) ?? '',
    companyName: asString(values['machineCompanyName']) ?? '',
    kafkaName: asString(values['machineKafkaName']) ?? undefined,
  };
  const machine: Machine =
    asString(values['machineName']) !== null
      ? flatMachine
      : ((values['machine'] as Machine | null | undefined) ?? flatMachine);

  const material: Material = (values['material'] as Material | null | undefined) ?? {
    name: '',
    isoCategory: '',
    designation: null,
    description: null,
    archived: false,
    companyName: '',
  };

  const materialLot: MaterialLot | null = (values['materialLot'] as MaterialLot | null | undefined) ?? null;

  return {
    toolId: asString(values['toolId']) ?? '',
    traceId,
    totalMachiningDuration: parseDuration(values['duration_total']),
    pieceMachiningDuration: parseDuration(values['duration_piece']),
    machinedPieces: asNumber(values['pieces']),
    machinedVolume: asNumber(values['volume']),
    machinedDistance: asNumber(values['distance']),
    malround: asNumber(values['malrond']),
    operator: asString(values['operator']),
    operation: asString(values['operation']),
    machine,
    material,
    materialLot,
    lubType: asString(values['lubType']) ?? '',
    lubBrand: asString(values['lubBrand']),
    holderType: asString(values['holderType']),
    holderBrand: asString(values['holderBrand']),
    clampingType: asString(values['clampingType']),
    clampingBrand: asString(values['clampingBrand']),
    ae: asNumber(values['ae']),
    ap: asNumber(values['ap']),
    vc: asNumber(values['vc']),
    fz: asNumber(values['fz']),
    toolState: {
      toolState: asString(values['toolState']) ?? 'unknown',
      changeReason: asStringArray(values['toolChange']),
      note: asString(values['commentary']),
    },
    createdAt: asString(values['createdAt']),
    updatedAt: null,
  };
}
